import { Inject, Component, Input, OnDestroy, OnInit, AfterViewInit, ChangeDetectorRef, DOCUMENT } from '@angular/core';

import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil, withLatestFrom, take, tap, filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { DocumentReader } from 'src/app/modules/core/store/models/document-reader';
import * as fromSharing from 'src/app/modules/sharing/store';
import {
  Template,
  RenderedTemplate,
  Communication,
  CommunicationStatus,
  ConfiguredChannel,
  ChannelType,
  ChannelUIds
} from 'src/app/modules/management/store/communication-center';
import { TemplateControlService } from 'src/app/modules/core/components/template-builder/controls/template-control.service';
import { DocumentSummaryWithDistance, DocumentSummaryWithEmail } from 'src/app/modules/cop/store/impacted-assets/impacted-assets.model';
import { NgxSpinnerService } from 'ngx-spinner';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import { ScreenshotService } from 'src/app/modules/core/services/screenshot.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'topo-share-document-dialog',
    templateUrl: './share-document-dialog.component.html',
    styleUrls: ['./share-document-dialog.component.scss'],
    standalone: false
})
export class ShareDocumentDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isActive: boolean;
  @Input() document: Document;
  @Input() pathToMap: string;
  @Input() mapUId: string;

  @Input() set dataset(dataset: Dataset) {
    this.dataset$.next(dataset);
  }
  dataset$: BehaviorSubject<Dataset> = new BehaviorSubject(null);

  distanceDropdownValue$: BehaviorSubject<any> = new BehaviorSubject(null);
  @Input() set distanceDropdownValue(value: any) {
    this.distanceDropdownValue$.next(value);
  }

  communicationMeta$: BehaviorSubject<any> = new BehaviorSubject(null);
  @Input() set communicationMeta(value: any) {
    this.communicationMeta$.next(value);
  }

  @Input() set isClosestAssetWorking(isWorking: boolean) {
    this._isClosestAssetWorking = isWorking;
    isWorking ? this.spinnerService.show() : this.spinnerService.hide();
  }
  _isClosestAssetWorking: boolean;

  selectedDocumentAssetsByDistance$: BehaviorSubject<any> = new BehaviorSubject(null);
  @Input() set selectedDocumentAssetsByDistance(value: any) {
    this.selectedDocumentAssetsByDistance$.next(value);
  }

  @Input() set closestAssetsWithEmail(assetEmails: any[]) {
    this.loadedAssetContacts = assetEmails;
    this.assetContactsForDisplay = assetEmails;

    if (!!assetEmails) {
      setTimeout(() => {
        this.selectAll(true);
      }, 100);
    }
  }

  loadedAssetsForTemplate: any[];
  recipientsInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  recipientsLoading = false;
  toSelectedRecipients: any[] = [];
  ccSelectedRecipients: any[] = [];
  bccSelectedRecipients: any[] = [];
  toReadOnly = false;
  ccReadOnly = false;
  selectedChannel: ConfiguredChannel;
  selectedTemplate: Template;
  organizationId: string;
  sendDocumentAttachments = false;
  assetNameLabel: string;
  distanceLabel: string;
  distListGroupName: string;
  userGroupName: string;
  channelTypeDisplay: string;
  preview: RenderedTemplate;
  subject: string;
  mapImage: string;
  loadedAssetContacts: DocumentSummaryWithEmail[] = [];
  assetContactsForDisplay: DocumentSummaryWithEmail[] = [];
  tabs: string[] = [];
  selectedTabIndex = 0;
  selectedContacts = [];
  isAllChecked = false;
  distanceValue: any = 50;
  units: string = 'miles';
  assets: string;
  isDrawn = false;
  distanceImperialOptions = [{ value: 5 }, { value: 10 }, { value: 25 }, { value: 50 }];
  distanceMetricOptions = [{ value: 10 }, { value: 15 }, { value: 40 }, { value: 80 }];
  distanceOptions = this.distanceImperialOptions;
  shortDistanceUnit: string;
  longDistanceUnit: string;
  isImperialUnit: boolean;
  showSubject: boolean;
  showCC: boolean;
  showRecipients: boolean;
  requireRecipients: boolean;
  enableUserMentions: boolean;
  showSlackHeader: boolean;
  showMicrosoftTeamsHeader: boolean;
  headerDatasetType: string;
  sortExpression: any;
  closestAssetTimeout: string | number | NodeJS.Timeout;

  constructor(
    @Inject(DOCUMENT) private domDocument: any,
    private store: Store<any>,
    private translate: TranslateService,
    private notify: SystemNotificationsService,
    public templateControlService: TemplateControlService,
    private spinnerService: NgxSpinnerService,
    private userProfileApiService: UserProfileApiService,
    private changeDetector: ChangeDetectorRef,
    private screenshotService: ScreenshotService
  ) {}

  ngOnInit(): void {
    this.assetNameLabel = this.translate.instant('MAP.ASSET-NAME');
    this.distanceLabel = this.translate.instant('MAP.DISTANCE-FROM-ALERT');
    this.sortExpression = null;

    this.templateControlService.changeDetector = this.changeDetector;

    this.store
      .select(fromUserProfile.selectDistanceUnit)
      .pipe(take(1))
      .subscribe(unit => {
        this.distanceValue = unit?.toLocaleLowerCase() === 'miles' ? 50 : 100;
        this.units = unit?.toLocaleLowerCase();
      });

    this.store
      .select(fromSharing.selectShowShareDocumentDialog)
      .pipe(
        withLatestFrom(
          this.store.select(fromSharing.selectSelectedChannel),
          this.store.select(fromSharing.selectSelectedTemplate),
          this.store.select(fromUserProfile.selectOrganization)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([showDialog, selectedChannel, selectedTemplate, organization]) => {
        this.isActive = showDialog;
        if (showDialog) {
          this.selectedChannel = selectedChannel;
          this.selectedTemplate = selectedTemplate;
          this.toSelectedRecipients = this.selectedTemplate?.settings?.toRecipients ?? [];
          this.ccSelectedRecipients = this.selectedTemplate?.settings?.ccRecipients ?? [];
          this.bccSelectedRecipients = this.selectedTemplate?.settings?.bccRecipients ?? [];
          this.organizationId = organization.uId;
          this.toReadOnly = this.selectedTemplate?.settings?.toReadOnly;
          this.ccReadOnly = this.selectedTemplate?.settings?.ccReadOnly;
          this.sendDocumentAttachments = this.selectedTemplate?.settings?.sendDocumentAttachments;

          this.showSubject = true;
          this.showCC = true;
          this.showRecipients = true;
          this.requireRecipients = true;
          this.enableUserMentions = false;
          this.showSlackHeader = false;
          this.showMicrosoftTeamsHeader = false;

          this.translate
            .get(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${selectedTemplate.channelType}`)
            .subscribe(val => (this.channelTypeDisplay = val));

          if (this.selectedChannel?.channelType === ChannelType.App && this.selectedChannel?.channelUId === ChannelUIds.SlackChannelUId) {
            this.showSubject = false;
            this.showCC = false;
            this.showRecipients = this.selectedChannel?.settings?.sendDirectMessages === true;
            this.requireRecipients = this.selectedChannel?.settings?.sendDirectMessages === true;
            this.enableUserMentions = true;
            this.showSlackHeader = true;
          }

          if (
            this.selectedChannel?.channelType === ChannelType.App &&
            this.selectedChannel?.channelUId === ChannelUIds.MicrosoftTeamsChannelUId
          ) {
            this.showSubject = false;
            this.showCC = false;
            this.showRecipients = this.selectedChannel?.settings?.sendDirectMessages === true;
            this.requireRecipients = this.selectedChannel?.settings?.sendDirectMessages === true;
            this.enableUserMentions = true;
            this.showMicrosoftTeamsHeader = true;
          }

          if (this.showRecipients) {
            this.tabs.push(this.translate.instant('MAP.MESSAGE-TAB'));
            this.tabs.push(this.translate.instant('MAP.RECIPIENTS-TAB'));
          } else {
            this.tabs.push(this.translate.instant('MAP.MESSAGE-TAB'));
          }

          this.subject = this.selectedTemplate?.settings?.subject;
          this.getTemplatePreview();
        }
      });

    this.store
      .select(fromSharing.selectCommunicationTemplatePreview)
      .pipe(takeUntil(this.destroy$))
      .subscribe(preview => {
        this.preview = preview;
        this.subject = this.preview?.subject;
        if (!!this.preview?.htmlContent) {
          const { htmlContent } = this.preview;
          this.templateControlService.dataset = this.dataset$.value;
          this.templateControlService.initializeFromLayoutTemplate(this.domDocument, htmlContent, true);

          const mapElement = document.querySelector('.map-container') as HTMLElement;

          // hide the map zoom elements when taking screenshot
          const gmControlElm = mapElement.querySelector('.gm-bundled-control');
          gmControlElm?.classList?.add('screenshot-hidden');

          const imageContent = this.screenshotService
            .takeScreenshot(mapElement)
            .pipe(take(1))
            .subscribe(imageContent => {
              this.mapImage = imageContent;
              gmControlElm?.classList?.remove('screenshot-hidden');
              this.changeDetector.markForCheck();
            });

          if (this.dataset$.value?.type !== DatasetType.Alerts) {
            this.loadedAssetContacts = [];
            this.assetContactsForDisplay = [];
          }

          this.generateClosestAssetTable();
        }
      });

    this.selectedDocumentAssetsByDistance$.pipe(takeUntil(this.destroy$)).subscribe(closestAssetData => {
      if (closestAssetData?.length > 0) {
        var allSummariesSorted = [] as DocumentSummaryWithDistance[];
        closestAssetData.forEach(d => {
          d.summariesWithDistance.forEach(asset => {
            allSummariesSorted.push(asset);
          });
        });

        allSummariesSorted.sort((a, b) => {
          return a.distance > b.distance ? 1 : -1;
        });

        this.loadedAssetsForTemplate = allSummariesSorted;
      } else {
        this.loadedAssetsForTemplate = [];
      }

      this.generateClosestAssetTable();
    });

    if (this.dataset$.value) {
      let datasetType = '';
      switch (this.dataset$.value.type) {
        case DatasetType.Alerts:
          datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-1-SINGULAR';
          break;
        case DatasetType.Assets:
          datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-2-SINGULAR';
          break;
        case DatasetType.PointOfInterest:
          datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-3';
          break;
        case DatasetType.Camera:
          datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-5';
          break;
        case DatasetType.Alarm:
          datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-6';
          break;
        case DatasetType.Incident:
          datasetType = 'MANAGEMENT.DATASETS.DATASET-TYPE-9';
          break;
      }

      this.translate.get(datasetType).subscribe(res => {
        this.headerDatasetType = res;
      });
    }

    this.translate.get(['MAP.DISTRIBUTION-LISTS', 'MAP.USERS']).subscribe(values => {
      this.distListGroupName = values['MAP.DISTRIBUTION-LISTS'];
      this.userGroupName = values['MAP.USERS'];
    });

    combineLatest([
      this.communicationMeta$,
      this.store.select(fromUserProfile.selectDistanceUnit),
      this.store.select(fromUserProfile.selectOrganization),
      this.distanceDropdownValue$
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([card]) => {
          return !!card;
        }),
        tap(([commMeta, unit, organization, distanceDropdownValue]) => {
          this.isDrawn = commMeta?.isDrawn;

          this.isImperialUnit = 'miles'.includes(unit?.toLocaleLowerCase());
          if (organization?.mapSettings) {
            this.distanceMetricOptions =
              organization.mapSettings.assetsImpactedDistanceOptionsInMetricUnits?.map(value => ({ value })) ?? this.distanceMetricOptions;
            this.distanceImperialOptions =
              organization.mapSettings.assetsImpactedDistanceOptionsInImperialUnits?.map(value => ({ value })) ??
              this.distanceImperialOptions;
          }

          this.shortDistanceUnit = this.isImperialUnit
            ? this.translate.instant('UNITS.DISTANCE.MILES-ABBREVIATED-LOWER')
            : this.translate.instant('UNITS.DISTANCE.KILOMETERS-ABBREVIATED-LOWER');
          this.longDistanceUnit = this.isImperialUnit
            ? this.translate.instant('UNITS.DISTANCE.MILES-CAMEL')
            : this.translate.instant('UNITS.DISTANCE.KILOMETERS-CAMEL');
          this.distanceOptions = (this.isImperialUnit ? this.distanceImperialOptions : this.distanceMetricOptions).sort((a, b) =>
            a.value > b.value ? 1 : -1
          );

          if (!distanceDropdownValue) {
            if (this.isImperialUnit) {
              if (commMeta?.metersWithin && !Number.isNaN(commMeta?.metersWithin) && commMeta?.metersWithin === 0) {
                this.distanceValue = 50;
              } else {
                this.distanceValue =
                  this.dataset$.value.assetsImpactedDistanceDefaultInImperialUnits ??
                  organization?.mapSettings?.assetsImpactedDistanceDefaultInImperialUnits;
              }
            } else {
              if (commMeta?.metersWithin && !Number.isNaN(commMeta?.metersWithin) && commMeta?.metersWithin === 0) {
                this.distanceValue = 100;
              } else {
                this.distanceValue =
                  this.dataset$.value.assetsImpactedDistanceDefaultInMetricUnits ??
                  organization?.mapSettings?.assetsImpactedDistanceDefaultInMetricUnits;
              }
            }
          } else {
            this.distanceValue = distanceDropdownValue;

            if (this.isImperialUnit && !this.distanceImperialOptions.find(o => o.value === distanceDropdownValue)) {
              this.distanceImperialOptions.push({ value: distanceDropdownValue });
            } else if (!this.isImperialUnit && !this.distanceMetricOptions.find(o => o.value === distanceDropdownValue)) {
              this.distanceMetricOptions.push({ value: distanceDropdownValue });
            }
          }
        })
      )
      .subscribe();
  }

  tabChanged(index: number): void {
    this.selectedTabIndex = index;
  }

  generateClosestAssetTable() {
    if (this.closestAssetTimeout) {
      clearTimeout(this.closestAssetTimeout);
    }

    this.closestAssetTimeout = setTimeout(() => {
      if (this.preview?.htmlContent?.includes('topo-selected-assets') && this.dataset$.value?.type === DatasetType.Alerts) {
        var assetHtml = `
          <table
            style="
              font-size: 14px;
              font-family: Verdana, Geneva, Tahoma, sans-serif;
              font-weight: 400;
              color: #000000;
              border-collapse: collapse;
              width:800px;"
            width="800" border="1" cellpadding="0" cellspacing="0">
            <tr>
              <td style="padding:4px; font-weight: 700;">${this.assetNameLabel}</td>
              <td style="padding:4px; font-weight: 700; min-width:165px;">${this.distanceLabel}</td>
            </tr>
          `;

          // if > 0
          if (this.loadedAssetsForTemplate?.length > 0) {
            this.loadedAssetsForTemplate.forEach(asset => {
              assetHtml += `
                <tr>
                  <td style="padding:4px;">${asset.name}</td>
                  <td style="padding:4px;" align="right">${asset.distanceText}</td>
                </tr>
                `;
            });
          }

          // if == 0
          if (this.loadedAssetsForTemplate?.length == 0) {
            assetHtml += `
              <tr>
                <td style="padding:4px;">${this.translate.instant('MAP.ASSETS-NONE-WITHIN-LIMIT')} </td>
                <td style="padding:4px;" align="right"></td>
              </tr>
            `;
          }

          assetHtml += '</table>';
          this.assets = assetHtml;
          this.changeDetector.markForCheck();
      }
    }, 0);
  }

  onChange() {
    const newAssetsForDisplay = this.loadedAssetContacts.filter(c => c.distance <= this.distanceValue);
    const oldAssetsForDisplay = this.assetContactsForDisplay;
    this.assetContactsForDisplay = newAssetsForDisplay ?? [];

    const valuesRemoved = oldAssetsForDisplay.filter(x => !newAssetsForDisplay.includes(x));
    valuesRemoved.forEach(item => {
      this.selectedContacts = this.selectedContacts.filter(e => e !== item.emailAddr + '-topoid-' + item.name);
    });

    if (this.sortExpression) {
      this.onSort(this.sortExpression);
    }

    this.isAllChecked = this.selectedContacts.length === this.assetContactsForDisplay.length;
  }

  onIsActiveClick(event, item) {
    if (event.target.checked) {
      this.selectedContacts.push(item.emailAddr + '-topoid-' + item.name);
    } else {
      let filteredList = this.selectedContacts.filter(e => e !== item.emailAddr + '-topoid-' + item.name);
      this.selectedContacts = filteredList;
    }

    this.isAllChecked = this.selectedContacts.length === this.assetContactsForDisplay.length;
  }

  ngAfterViewInit(): void {
    const editorElement = document.getElementById('share-message');
    const toolbarElement = document.getElementById('share-message-toolbar');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.templateControlService.changeDetector = null;
  }

  close(): void {
    this.store.dispatch(fromSharing.hideShareDocumentDialog());
  }

  getTemplatePreview(): void {
    const reader = new DocumentReader(this.document);
    const documentUId = reader.uId;

    if (!this.selectedTemplate.uId) {
      return;
    }

    const communication: Communication = {
      uId: null,
      name: null,
      description: null,
      configuredChannelUId: this.selectedChannel.uId,
      communicationTemplateUId: this.selectedTemplate.uId,
      datasetUId: this.dataset$.value.uId,
      documentUId,
      documentChangeUId: null,
      customSubject: null,
      customDocument: null,
      toDistributionListUIds: [],
      ccDistributionListUIds: [],
      bccDistributionListUIds: [],
      toCustomRecipients: [],
      ccCustomRecipients: [],
      bccCustomRecipients: [],
      toUserUIds: [],
      ccUserUIds: [],
      bccUserUIds: [],
      status: CommunicationStatus.None,
      communicationStartTime: null,
      communicationCompletionTime: null,
      renderedTemplate: null,
      pathToMap: this.pathToMap,
      mapUId: this.mapUId
    };

    this.store.dispatch(fromSharing.getCommunicationTemplatePreviewFromApi({ communication }));
  }

  sendClick(): void {
    const reader = new DocumentReader(this.document);
    const documentUId = reader.uId;

    if (this.requireRecipients && this.toSelectedRecipients?.length === 0 && this.selectedContacts?.length === 0) {
      this.translate
        .get('MAP.ERROR-COMMUNICATION-RECIPIENTS-REQUIRED')
        .subscribe(val => this.notify.error(val));
      return;
    }

    const toDistributionListUIds = [];
    const ccDistributionListUIds = [];
    const bccDistributionListUIds = [];
    const toUserUIds = [];
    const ccUserUIds = [];
    const bccUserUIds = [];
    const toCustomRecipients = [];
    const ccCustomRecipients = [];
    const bccCustomRecipients = [];

    for (const recipient of this.toSelectedRecipients) {
      if (!recipient.type) {
        toCustomRecipients.push(recipient.displayName);
      } else if (recipient.type === this.distListGroupName) {
        toDistributionListUIds.push(recipient.uId);
      } else if (recipient.type === this.userGroupName) {
        toUserUIds.push(recipient.uId);
      }
    }

    for (const assetEmail of this.selectedContacts) {
      var newstr = assetEmail.replace(/-topoid-.*$/, '');
      toCustomRecipients.push(newstr);
    }

    for (const recipient of this.ccSelectedRecipients) {
      if (!recipient.type) {
        ccCustomRecipients.push(recipient.displayName);
      } else if (recipient.type === this.distListGroupName) {
        ccDistributionListUIds.push(recipient.uId);
      } else if (recipient.type === this.userGroupName) {
        ccUserUIds.push(recipient.uId);
      }
    }

    for (const recipient of this.bccSelectedRecipients) {
      if (!recipient.type) {
        bccCustomRecipients.push(recipient.displayName);
      } else if (recipient.type === this.distListGroupName) {
        bccDistributionListUIds.push(recipient.uId);
      } else if (recipient.type === this.userGroupName) {
        bccUserUIds.push(recipient.uId);
      }
    }

    const layoutContentElement = document.getElementById('message-template');
    const layoutContent = layoutContentElement.outerHTML;

    const communication: Communication = {
      uId: null,
      name: null,
      description: null,
      configuredChannelUId: this.selectedChannel.uId,
      communicationTemplateUId: this.selectedTemplate.uId,
      datasetUId: this.dataset$.value.uId,
      documentUId,
      documentChangeUId: null,
      customSubject: this.subject,
      customHtmlContent: layoutContent,
      customTextContent: this.preview.textContent,
      customDocument: null,
      toDistributionListUIds,
      ccDistributionListUIds,
      bccDistributionListUIds,
      toUserUIds,
      ccUserUIds,
      bccUserUIds,
      toCustomRecipients,
      ccCustomRecipients,
      bccCustomRecipients,
      status: CommunicationStatus.None,
      communicationStartTime: null,
      communicationCompletionTime: null,
      renderedTemplate: null,
      sendAsGroup: !!this.selectedTemplate.settings.sendAsGroup,
      sendDocumentAttachments: !!this.selectedTemplate.settings.sendDocumentAttachments,
      pathToMap: this.pathToMap,
      mapUId: this.mapUId
    };

    this.store.dispatch(fromSharing.sendCommunicationWithApi({ communication }));

    this.loadedAssetContacts = [];
    this.assetContactsForDisplay = [];
    this.loadedAssetsForTemplate = [];

    this.store.dispatch(fromSharing.hideShareDocumentDialog());
  }

  cancelClick(): void {
    this.loadedAssetContacts = [];
    this.assetContactsForDisplay = [];
    this.loadedAssetsForTemplate = [];

    this.store.dispatch(fromSharing.hideShareDocumentDialog());
  }

  contentScrolled(): void {
    this.templateControlService.hideSettingsControlIfNotEdit();
  }

  onSort(sortExpression: { sortField: string; sortDirection: string }): void {
    var arrayForSort = [...this.assetContactsForDisplay];

    this.sortExpression = sortExpression;

    if (sortExpression.sortDirection === 'Ascending') {
      arrayForSort.sort((a, b) =>
        a[sortExpression.sortField.toLocaleLowerCase()] < b[sortExpression.sortField.toLocaleLowerCase()] ? 1 : -1
      );
    } else {
      arrayForSort.sort((a, b) =>
        a[sortExpression.sortField.toLocaleLowerCase()] > b[sortExpression.sortField.toLocaleLowerCase()] ? 1 : -1
      );
    }

    this.assetContactsForDisplay = arrayForSort;
  }

  selectAll(event: any): void {
    this.selectedContacts = [];

    this.assetContactsForDisplay.forEach(x => {
      if (!document.getElementById('email-' + x.emailAddr + '-' + x.name)) {
        return;
      }
      (document.getElementById('email-' + x.emailAddr + '-' + x.name) as HTMLInputElement).checked = event;

      if (event) {
        this.selectedContacts.push(x.emailAddr + '-topoid-' + x.name);
      }
    });

    this.isAllChecked = event;
  }

  getUserList(searchText, renderList) {
    this.userProfileApiService.getUsersForMention(this.organizationId, searchText).subscribe(res => {
      const entries = res.data.map(p => ({ id: p.uId, value: `${p.displayName}` }));
      renderList(entries);
    });
  }
}
